/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { CookieBanner } from '../components/layout/LegalLayout';
import {
  Search, FolderOpen, Cloud, Zap,
  ChevronRight, Check, Menu, X, Globe, Twitter,
  Instagram, Mail, ArrowRight, Star, ChevronDown, Layers
} from 'lucide-react';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  amber100: '#FFF4E0',
  amber200: '#FFD88A',
  amber300: '#F5A623',
  amber400: '#C4811A',
  amber500: '#8B5A0A',
  ink:      '#1A1814',
  inkLight: '#3D3A34',
  muted:    '#7A7570',
  border:   '#E8E4DC',
  surface:  '#FAFAF8',
  white:    '#FFFFFF',
};

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo: React.FC<{ dark?: boolean }> = ({ dark }) => (
  <a href="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
    <div style={{
      width:34, height:34, borderRadius:10,
      background:`linear-gradient(145deg,${C.amber200},${C.amber400})`,
      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
    }}>
      <svg width="17" height="17" viewBox="0 0 180 180" fill="none">
        <g transform="translate(90,88) rotate(-35)">
          <rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/>
          <rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/>
          <polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/>
        </g>
        <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
        <g transform="translate(126,124) rotate(45)">
          <rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/>
        </g>
      </svg>
    </div>
    <span style={{
      fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:22,
      letterSpacing:'-0.06em', color: dark ? C.white : C.ink,
      lineHeight:1
    }}>Jötr</span>
  </a>
);

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn, { passive:true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <nav style={{
      position:'fixed', inset:'0 0 auto 0', zIndex:100,
      transition:'all .25s',
      background: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      borderBottom: scrolled ? `1px solid ${C.border}` : '1px solid transparent',
    }}>
      <div style={{ maxWidth:1120, margin:'0 auto', padding:'0 24px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <Logo/>
        <div style={{ display:'flex', alignItems:'center', gap:32, fontFamily:"'DM Sans',sans-serif" }} className="desktop-nav">
          {['Features','How it works','Pricing'].map(l => (
            <a key={l} href={`#${l.toLowerCase().replace(/ /g,'-')}`} style={{ fontSize:14, fontWeight:500, color:C.inkLight, textDecoration:'none', transition:'color .15s' }}
              onMouseEnter={e=>(e.currentTarget.style.color=C.amber400)}
              onMouseLeave={e=>(e.currentTarget.style.color=C.inkLight)}>{l}</a>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }} className="desktop-nav">
          <a href="/login" style={{ fontSize:14, fontWeight:600, color:C.inkLight, textDecoration:'none', padding:'8px 16px' }}>Log in</a>
          <a href="/signup" style={{
            fontSize:13, fontWeight:700, color:C.white, textDecoration:'none',
            padding:'9px 20px', borderRadius:10,
            background:`linear-gradient(135deg,${C.amber300},${C.amber400})`,
            letterSpacing:'0.01em', fontFamily:"'DM Sans',sans-serif"
          }}>Get started free</a>
        </div>
        <button onClick={()=>setOpen(o=>!o)} className="mobile-menu-btn"
          style={{ background:'none', border:'none', cursor:'pointer', color:C.ink, padding:4 }}>
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>
      {open && (
        <div style={{ background:C.white, borderTop:`1px solid ${C.border}`, padding:'16px 24px 20px', display:'flex', flexDirection:'column', gap:4 }}>
          {['Features','How it works','Pricing'].map(l => (
            <a key={l} href={`#${l.toLowerCase().replace(/ /g,'-')}`}
              onClick={()=>setOpen(false)}
              style={{ fontSize:16, fontWeight:500, color:C.inkLight, textDecoration:'none', padding:'10px 4px', borderBottom:`1px solid ${C.border}` }}>{l}</a>
          ))}
          <div style={{ display:'flex', flexDirection:'column', gap:8, paddingTop:12 }}>
            <a href="/login" style={{ textAlign:'center', padding:'12px', borderRadius:10, border:`1px solid ${C.border}`, fontWeight:600, color:C.ink, textDecoration:'none', fontSize:15 }}>Log in</a>
            <a href="/signup" style={{ textAlign:'center', padding:'12px', borderRadius:10, fontWeight:700, color:C.white, textDecoration:'none', fontSize:15, background:`linear-gradient(135deg,${C.amber300},${C.amber400})` }}>Get started free →</a>
          </div>
        </div>
      )}
    </nav>
  );
};

// ─── HERO ─────────────────────────────────────────────────────────────────────
const SEARCH_DEMOS = ['IRR formula', 'client meeting Q3', 'physics equations', 'launch checklist', 'project budget'];

const Hero: React.FC = () => {
  const [idx, setIdx]   = useState(0);
  const [vis, setVis]   = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setVis(false);
      setTimeout(() => { setIdx(i=>(i+1)%SEARCH_DEMOS.length); setVis(true); }, 300);
    }, 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{ minHeight:'100vh', display:'flex', flexDirection:'column', justifyContent:'center', padding:'100px 24px 60px', background:`linear-gradient(160deg, #FFFDF8 0%, #FFF6E0 60%, #FDECC8 100%)`, position:'relative', overflow:'hidden' }}>
      {/* bg blobs */}
      <div style={{ position:'absolute', top:-80, right:-120, width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,#FFE09A44,transparent 70%)', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', bottom:-80, left:-80, width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,#F5A62322,transparent 70%)', pointerEvents:'none' }}/>

      <div style={{ maxWidth:1120, margin:'0 auto', width:'100%' }}>
        <div className="hero-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:60, alignItems:'center' }}>

          {/* LEFT */}
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 14px', borderRadius:20, background:C.amber100, border:`1px solid ${C.amber200}`, marginBottom:28 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:C.amber300, display:'inline-block' }}/>
              <span style={{ fontSize:12, fontWeight:700, color:C.amber500, fontFamily:"'DM Sans',sans-serif", letterSpacing:'0.04em' }}>FREE FOREVER · NO CREDIT CARD</span>
            </div>

            <h1 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(38px,5vw,62px)', lineHeight:1.06, letterSpacing:'-0.04em', color:C.ink, marginBottom:22 }}>
              Write it once.<br/>
              <span style={{ background:`linear-gradient(90deg,${C.amber300},${C.amber400})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Find it forever.</span>
            </h1>

            <p style={{ fontSize:'clamp(15px,1.5vw,18px)', color:C.muted, lineHeight:1.7, marginBottom:12, maxWidth:460, fontFamily:"'DM Sans',sans-serif", fontWeight:400 }}>
              Jötr is the note app where every word is instantly searchable. Stop losing your best ideas, formulas, and notes — find any of them in seconds.
            </p>

            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:32, fontFamily:"'DM Sans',sans-serif" }}>
              <Globe size={14} color={C.amber400}/>
              <span style={{ fontSize:14, fontWeight:600, color:C.amber400 }}>Your notes sync to every device — log in from anywhere, anytime.</span>
            </div>

            <div className="hero-ctas" style={{ display:'flex', gap:12, marginBottom:36, flexWrap:'wrap' }}>
              <a href="/signup" style={{
                display:'inline-flex', alignItems:'center', gap:8, padding:'14px 28px', borderRadius:12,
                background:`linear-gradient(135deg,${C.amber300},${C.amber400})`,
                color:C.white, fontWeight:700, fontSize:16, textDecoration:'none',
                fontFamily:"'DM Sans',sans-serif", letterSpacing:'-0.01em',
                boxShadow:`0 8px 24px ${C.amber300}55`
              }}>Start writing free <ChevronRight size={17}/></a>
              <a href="#how-it-works" style={{
                display:'inline-flex', alignItems:'center', gap:8, padding:'14px 24px', borderRadius:12,
                border:`1.5px solid ${C.border}`, color:C.inkLight, fontWeight:600, fontSize:15,
                textDecoration:'none', fontFamily:"'DM Sans',sans-serif", background:C.white
              }}>See how it works</a>
            </div>

            <div style={{ display:'flex', flexWrap:'wrap', gap:20 }}>
              {['No signup friction','Works offline','All your devices'].map(t => (
                <span key={t} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>
                  <Check size={13} color="#22C55E"/> {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — App mockup */}
          <div style={{ position:'relative' }}>
            <div style={{ borderRadius:20, overflow:'hidden', border:`1px solid ${C.border}`, background:C.white, boxShadow:'0 24px 80px rgba(0,0,0,0.10)' }}>
              {/* Browser chrome */}
              <div style={{ background:'#F5F3EF', padding:'11px 16px', display:'flex', alignItems:'center', gap:8, borderBottom:`1px solid ${C.border}` }}>
                <div style={{ display:'flex', gap:5 }}>
                  {['#FF5F57','#FEBC2E','#28C840'].map(c=><div key={c} style={{ width:11, height:11, borderRadius:'50%', background:c }}/>)}
                </div>
                <div style={{ flex:1, display:'flex', justifyContent:'center' }}>
                  <div style={{ background:C.white, borderRadius:6, padding:'3px 14px', fontSize:11, color:C.muted, border:`1px solid ${C.border}`, fontFamily:"'DM Sans',sans-serif" }}>jotr.online</div>
                </div>
              </div>

              <div style={{ display:'flex', height:360 }}>
                {/* Sidebar */}
                <div style={{ width:156, background:'#FAFAF8', borderRight:`1px solid ${C.border}`, padding:14, flexShrink:0, display:'flex', flexDirection:'column', gap:4 }}>
                  <p style={{ fontSize:9, fontWeight:800, color:C.muted, letterSpacing:'0.1em', padding:'0 6px 6px', textTransform:'uppercase', fontFamily:"'DM Sans',sans-serif" }}>My folders</p>
                  {[{n:'Work notes',c:'#F5A623',k:24},{n:'Formulas',c:'#C4811A',k:12},{n:'Study',c:'#16A34A',k:8},{n:'Personal',c:'#7C3AED',k:31},{n:'Journal',c:'#DB2777',k:5}].map(f=>(
                    <div key={f.n} style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 8px', borderRadius:7, cursor:'pointer', transition:'background .1s' }}
                      onMouseEnter={e=>(e.currentTarget.style.background='#F0EDE6')} onMouseLeave={e=>(e.currentTarget.style.background='transparent')}>
                      <div style={{ width:7, height:7, borderRadius:2, background:f.c, flexShrink:0 }}/>
                      <span style={{ fontSize:11, color:C.inkLight, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>{f.n}</span>
                      <span style={{ fontSize:10, color:C.muted }}>{f.k}</span>
                    </div>
                  ))}
                  <div style={{ marginTop:'auto', padding:'8px', borderRadius:8, background:C.amber100, border:`1px solid ${C.amber200}` }}>
                    <p style={{ fontSize:9, fontWeight:700, color:C.amber500, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2, fontFamily:"'DM Sans',sans-serif" }}>Storage</p>
                    <div style={{ height:3, background:C.border, borderRadius:2 }}>
                      <div style={{ width:'34%', height:'100%', background:C.amber300, borderRadius:2 }}/>
                    </div>
                    <p style={{ fontSize:9, color:C.amber400, marginTop:2, fontFamily:"'DM Sans',sans-serif" }}>34 MB of 200 MB</p>
                  </div>
                </div>

                {/* Main */}
                <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
                  {/* Search bar */}
                  <div style={{ padding:'12px 14px 10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 14px', borderRadius:10, background:C.amber100, border:`1.5px solid ${C.amber200}` }}>
                      <Search size={14} color={C.amber400} style={{ flexShrink:0 }}/>
                      <span style={{ fontSize:13, fontWeight:500, color:C.amber400, fontFamily:"'DM Sans',sans-serif", opacity: vis ? 1 : 0, transition:'opacity .25s' }}>{SEARCH_DEMOS[idx]}</span>
                      <span style={{ width:1.5, height:14, background:C.amber300, display:'inline-block', marginLeft:1, animation:'blink 1s step-end infinite' }}/>
                    </div>
                  </div>

                  {/* Results */}
                  <div style={{ flex:1, padding:'0 12px 12px', display:'flex', flexDirection:'column', gap:7, overflowY:'hidden' }}>
                    <p style={{ fontSize:10, fontWeight:600, color:C.muted, padding:'0 2px', fontFamily:"'DM Sans',sans-serif" }}>3 results · <span style={{ color:C.amber400 }}>all notes</span></p>
                    {[
                      { t:'Q3 Financial Model', s:'The IRR formula calculates internal rate of return on cash flows...', tag:'Work notes', tc:'#F5A623' },
                      { t:'Excel Reference Sheet', s:'Quick reference: IRR formula requires at least one positive value...', tag:'Formulas', tc:'#C4811A' },
                      { t:'MBA Notes — Finance', s:'Comparing NPV and IRR formula — discount rate sensitivity analysis...', tag:'Study', tc:'#16A34A' },
                    ].map((r,i)=>(
                      <div key={i} style={{ padding:'10px 12px', borderRadius:10, border:`1px solid ${i===0?C.amber200:C.border}`, background: i===0?C.amber100:C.white, cursor:'pointer' }}>
                        <p style={{ fontSize:12, fontWeight:700, color:C.ink, marginBottom:3, fontFamily:"'DM Sans',sans-serif" }}>{r.t}</p>
                        <p style={{ fontSize:11, color:C.muted, lineHeight:1.45, marginBottom:6, fontFamily:"'DM Sans',sans-serif" }}>
                          {r.s.split('IRR')[0]}
                          <mark style={{ background:'#FFD88A', color:C.amber500, padding:'0 2px', borderRadius:3, fontWeight:700 }}>IRR</mark>
                          {r.s.split('IRR').slice(1).join('IRR')}
                        </p>
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:r.tc+'22', color:r.tc, fontFamily:"'DM Sans',sans-serif" }}>{r.tag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating sync badge */}
            <div style={{ position:'absolute', bottom:-14, left:-20, background:C.white, borderRadius:16, padding:'10px 14px', display:'flex', alignItems:'center', gap:10, border:`1px solid ${C.border}`, boxShadow:'0 8px 24px rgba(0,0,0,0.08)' }}>
              <div style={{ width:34, height:34, borderRadius:10, background:'#DCFCE7', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Cloud size={16} color="#16A34A"/>
              </div>
              <div>
                <p style={{ fontSize:12, fontWeight:700, color:C.ink, margin:0, fontFamily:"'DM Sans',sans-serif" }}>Synced everywhere</p>
                <p style={{ fontSize:10, color:C.muted, margin:0, fontFamily:"'DM Sans',sans-serif" }}>Phone · Tablet · Desktop</p>
              </div>
            </div>

            {/* Floating note count */}
            <div style={{ position:'absolute', top:-16, right:-16, background:C.white, borderRadius:14, padding:'9px 14px', border:`1px solid ${C.border}`, boxShadow:'0 8px 24px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize:11, color:C.muted, margin:'0 0 1px', fontFamily:"'DM Sans',sans-serif" }}>Total notes</p>
              <p style={{ fontSize:22, fontWeight:800, color:C.ink, margin:0, letterSpacing:'-0.04em', fontFamily:"'Sora',sans-serif" }}>80</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </section>
  );
};

// ─── SOCIAL PROOF BAR ─────────────────────────────────────────────────────────
const SocialProof: React.FC = () => (
  <section style={{ padding:'28px 24px', background:C.white, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
    <div style={{ maxWidth:1120, margin:'0 auto', display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'center', gap:'16px 40px' }}>
      <span style={{ fontSize:13, fontWeight:600, color:C.muted, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>Trusted by professionals & students across Africa</span>
      {[
        { n:'10,000+', l:'Notes created' },
        { n:'4.8★', l:'User rating' },
        { n:'100%', l:'Free at launch' },
        { n:'All devices', l:'Web · Android · iOS soon' },
      ].map(s=>(
        <div key={s.n} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
          <span style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:18, color:C.ink, letterSpacing:'-0.04em' }}>{s.n}</span>
          <span style={{ fontSize:11, color:C.muted, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>{s.l}</span>
        </div>
      ))}
    </div>
  </section>
);

// ─── FEATURE DEEP-DIVES ───────────────────────────────────────────────────────
const FEATURES = [
  {
    tag:'Search', icon:<Search size={18}/>,
    title:'Find any word in any note — instantly.',
    body:"Type a word and Jötr scans every note you've ever written in real time. Results show the matched sentence with your keyword highlighted in amber. Click a result and the note opens scrolled directly to that line. No more endless scrolling.",
    bullets:['Live results as you type — no pressing Enter','Keyword highlighted in amber in results and inside the note','Filter by folder or tag to narrow scope','Scroll-to-match on note open'],
    mockup: (
      <div style={{ background:C.surface, borderRadius:16, padding:20, border:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:10, background:C.amber100, border:`1.5px solid ${C.amber200}`, marginBottom:14 }}>
          <Search size={14} color={C.amber400}/><span style={{ fontSize:13, color:C.amber400, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>quarterly targets</span>
        </div>
        {[{t:'Business Plan 2026',m:'...our quarterly targets are set at 20% growth...'},{t:'OKR Notes',m:'...reviewed quarterly targets with team last Tuesday...'},{t:'Investor Update',m:'...exceeded quarterly targets for Q2 by 14%...'}].map((r,i)=>(
          <div key={i} style={{ padding:'10px 12px', borderRadius:10, background: i===0?C.amber100:C.white, border:`1px solid ${i===0?C.amber200:C.border}`, marginBottom:8 }}>
            <p style={{ fontSize:12, fontWeight:700, color:C.ink, marginBottom:3, fontFamily:"'DM Sans',sans-serif" }}>{r.t}</p>
            <p style={{ fontSize:11, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>{r.m.split('quarterly targets')[0]}<mark style={{ background:'#FFD88A', color:C.amber500, padding:'0 2px', borderRadius:3, fontWeight:700 }}>quarterly targets</mark>{r.m.split('quarterly targets')[1]}</p>
          </div>
        ))}
      </div>
    )
  },
  {
    tag:'Organisation', icon:<FolderOpen size={18}/>,
    title:'Folders inside folders. Your structure, your rules.',
    body:'Build a filing system that matches exactly how your brain works. Create folders for every area of your life, then sub-folders inside those, down to any depth you need. Drag notes between folders or use tags to cross-reference across everything.',
    bullets:['Unlimited folders and sub-folders, any depth','Colour-coded folders for instant visual navigation','Tags for cross-folder categorisation','Drag-and-drop note organisation'],
    mockup: (
      <div style={{ background:C.surface, borderRadius:16, padding:20, border:`1px solid ${C.border}` }}>
        {[{name:'Work',color:'#F5A623',sub:['Meetings','Projects','Formulas']},{name:'Study',color:'#16A34A',sub:['Physics','Maths','History']},{name:'Personal',color:'#7C3AED',sub:['Journal','Goals']}].map((f,fi)=>(
          <div key={f.name} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, background: fi===0?C.amber100:C.white, border:`1px solid ${fi===0?C.amber200:C.border}` }}>
              <FolderOpen size={13} color={f.color}/><span style={{ fontSize:12, fontWeight:700, color:C.ink, fontFamily:"'DM Sans',sans-serif" }}>{f.name}</span>
              <span style={{ marginLeft:'auto', fontSize:10, color:f.color, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>{f.sub.length} sub-folders</span>
            </div>
            {fi===0 && f.sub.map(s=>(
              <div key={s} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px 6px 28px', marginTop:3, borderRadius:7, background:C.white, border:`1px solid ${C.border}` }}>
                <div style={{ width:4, height:4, borderRadius:'50%', background:f.color }}/>
                <span style={{ fontSize:11, color:C.inkLight, fontFamily:"'DM Sans',sans-serif" }}>{s}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  },
  {
    tag:'Sync everywhere', icon:<Cloud size={18}/>,
    title:'Write on your laptop. Find it on your phone.',
    body:"Every note is saved to the cloud the moment you type it. Log in from any browser on any device and everything is there — exactly as you left it. Working without internet? Jötr caches your notes locally and syncs the moment you're back online.",
    bullets:['Instant cloud sync after every keystroke','Works offline — reads and writes without internet','Syncs automatically when connection returns','One account, unlimited devices'],
    mockup: (
      <div style={{ display:'flex', gap:14, alignItems:'flex-end' }}>
        {/* Phone */}
        <div style={{ width:110, borderRadius:18, border:`2px solid ${C.border}`, background:C.white, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.08)', flexShrink:0 }}>
          <div style={{ background:C.ink, height:18, display:'flex', justifyContent:'center', alignItems:'center' }}>
            <div style={{ width:36, height:4, borderRadius:2, background:'#444' }}/>
          </div>
          <div style={{ padding:'10px 8px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 8px', borderRadius:7, background:C.amber100, marginBottom:8 }}>
              <Search size={10} color={C.amber400}/><span style={{ fontSize:9, color:C.amber400, fontFamily:"'DM Sans',sans-serif" }}>Search notes...</span>
            </div>
            {['Meeting notes','Q3 Budget','Formulas'].map(n=>(
              <div key={n} style={{ padding:'6px 8px', borderRadius:6, border:`1px solid ${C.border}`, marginBottom:4 }}>
                <p style={{ fontSize:9, fontWeight:700, color:C.ink, margin:0, fontFamily:"'DM Sans',sans-serif" }}>{n}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Laptop */}
        <div style={{ flex:1, borderRadius:12, border:`2px solid ${C.border}`, background:C.white, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ background:'#F0EDE6', height:14, display:'flex', alignItems:'center', padding:'0 8px', gap:4 }}>
            {['#FF5F57','#FEBC2E','#28C840'].map(c=><div key={c} style={{ width:7, height:7, borderRadius:'50%', background:c }}/>)}
          </div>
          <div style={{ padding:'10px 10px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:7, background:C.amber100, marginBottom:8 }}>
              <Search size={11} color={C.amber400}/><span style={{ fontSize:10, color:C.amber400, fontFamily:"'DM Sans',sans-serif" }}>Search notes...</span>
            </div>
            {['Meeting notes','Q3 Budget','Formulas','Journal - Monday'].map(n=>(
              <div key={n} style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, marginBottom:4 }}>
                <p style={{ fontSize:10, fontWeight:700, color:C.ink, margin:0, fontFamily:"'DM Sans',sans-serif" }}>{n}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Sync line */}
        <div style={{ position:'absolute', left:'50%', top:'50%', transform:'translate(-50%,-50%)', display:'flex', alignItems:'center', gap:4 }}>
        </div>
      </div>
    )
  },
  {
    tag:'Editor', icon:<Zap size={18}/>,
    title:'A fast, clean editor that gets out of your way.',
    body:'Jötr\'s editor opens in under a second and auto-saves every keystroke. Format with headings, bold, bullet lists, numbered lists, and inline code blocks without ever reaching for a mouse. Dark mode included — write comfortably in any light.',
    bullets:['Opens instantly — no loading screens','Auto-saves every keystroke, no manual save button','Rich formatting: headings, bold, lists, code','Dark mode with one toggle'],
    mockup: (
      <div style={{ background:C.surface, borderRadius:16, border:`1px solid ${C.border}`, overflow:'hidden' }}>
        <div style={{ padding:'10px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:8 }}>
          {['B','I','H1','H2','• —','1.','</>'].map(b=>(
            <button key={b} style={{ padding:'3px 8px', borderRadius:5, border:`1px solid ${C.border}`, background:b==='B'?C.amber100:C.white, color:b==='B'?C.amber500:C.inkLight, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>{b}</button>
          ))}
        </div>
        <div style={{ padding:20 }}>
          <p style={{ fontSize:15, fontWeight:800, color:C.ink, marginBottom:10, fontFamily:"'Sora',sans-serif" }}>Q3 Financial Model Notes</p>
          <p style={{ fontSize:12, color:C.inkLight, marginBottom:8, fontFamily:"'DM Sans',sans-serif", lineHeight:1.6 }}>Key formulas to remember for this quarter's model:</p>
          {['IRR: =IRR(cashflows, guess)','NPV: =NPV(rate, values)','WACC: weighted average of equity and debt costs'].map((l,i)=>(
            <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:5 }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:C.amber300, flexShrink:0, marginTop:6 }}/>
              <span style={{ fontSize:11, color:C.inkLight, fontFamily:'monospace', background:C.amber100, padding:'1px 6px', borderRadius:4 }}>{l}</span>
            </div>
          ))}
          <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:8, height:14, background:C.amber300, borderRadius:1, animation:'blink 1s step-end infinite' }}/>
          </div>
        </div>
        <div style={{ padding:'6px 16px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between' }}>
          <span style={{ fontSize:10, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>Saved just now</span>
          <span style={{ fontSize:10, color:'#22C55E', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>✓ Synced</span>
        </div>
      </div>
    )
  },
];

const FeatureDeepDives: React.FC = () => (
  <section id="features" style={{ padding:'100px 24px', background:C.white }}>
    <div style={{ maxWidth:1120, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:72 }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(30px,4vw,48px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:16 }}>
          Built around search.<br/>Designed for real life.
        </h2>
        <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif", maxWidth:500, margin:'0 auto', lineHeight:1.7 }}>
          Everything you need. Nothing you don't. Four features done properly.
        </p>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:90 }}>
        {FEATURES.map((f,i)=>(
          <div key={i} className="feature-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:60, alignItems:'center', direction: i%2===1 ? 'rtl' : 'ltr' }}>
            <div style={{ direction:'ltr' }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'5px 13px', borderRadius:20, background:C.amber100, border:`1px solid ${C.amber200}`, marginBottom:18 }}>
                <span style={{ color:C.amber400 }}>{f.icon}</span>
                <span style={{ fontSize:12, fontWeight:700, color:C.amber500, fontFamily:"'DM Sans',sans-serif", letterSpacing:'0.04em', textTransform:'uppercase' }}>{f.tag}</span>
              </div>
              <h3 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(22px,2.5vw,32px)', letterSpacing:'-0.03em', color:C.ink, marginBottom:16, lineHeight:1.2 }}>{f.title}</h3>
              <p style={{ fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:24, fontFamily:"'DM Sans',sans-serif" }}>{f.body}</p>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {f.bullets.map(b=>(
                  <div key={b} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:20, height:20, borderRadius:6, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Check size={11} color={C.amber400}/>
                    </div>
                    <span style={{ fontSize:14, color:C.inkLight, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ direction:'ltr' }}>{f.mockup}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── USE CASES ────────────────────────────────────────────────────────────────
const USECASES = [
  { title:'For professionals', icon:<Layers size={20}/>, color:'#3B82F6', bg:'#EFF6FF',
    desc:'Business analysts, consultants, managers — keep all your formulas, meeting notes, and frameworks in one searchable place. Find a specific formula across 200 notes in under two seconds.',
    list:['Search formulas and frameworks by keyword','Organise by client, project, or quarter','Access your notes on any work device'] },
  { title:'For students', icon:<Zap size={20}/>, color:'#16A34A', bg:'#F0FDF4',
    desc:'Lecture notes, revision summaries, assignment research — stored in subject folders and fully searchable. No more hunting through notebooks before an exam.',
    list:['Folder per subject, sub-folders per topic','Search any concept across all your lectures','Tag notes by exam date or module'] },
  { title:'For entrepreneurs', icon:<Globe size={20}/>, color:'#7C3AED', bg:'#F5F3FF',
    desc:'Track ideas, client notes, meeting summaries, and action items all in one place. Access your business brain from your phone, laptop, or any device on the go.',
    list:['One place for clients, ideas, and tasks','Access from phone, tablet, or desktop','Share notes as read-only links (coming soon)'] },
];

const UseCases: React.FC = () => (
  <section style={{ padding:'100px 24px', background:C.surface }}>
    <div style={{ maxWidth:1120, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:60 }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,3.5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:14 }}>Who uses Jötr?</h2>
        <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif", maxWidth:460, margin:'0 auto' }}>Built for everyone who writes things down and needs to find them again.</p>
      </div>
      <div className="usecase-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:24 }}>
        {USECASES.map((u,i)=>(
          <div key={i} style={{ padding:32, borderRadius:20, border:`1px solid ${C.border}`, background:C.white, transition:'transform .2s, box-shadow .2s' }}
            onMouseEnter={e=>{ (e.currentTarget as HTMLDivElement).style.transform='translateY(-4px)'; (e.currentTarget as HTMLDivElement).style.boxShadow='0 16px 40px rgba(0,0,0,0.08)'; }}
            onMouseLeave={e=>{ (e.currentTarget as HTMLDivElement).style.transform=''; (e.currentTarget as HTMLDivElement).style.boxShadow=''; }}>
            <div style={{ width:46, height:46, borderRadius:13, background:u.bg, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20, color:u.color }}>{u.icon}</div>
            <h3 style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:20, letterSpacing:'-0.03em', color:C.ink, marginBottom:12 }}>{u.title}</h3>
            <p style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:20, fontFamily:"'DM Sans',sans-serif" }}>{u.desc}</p>
            {u.list.map(l=>(
              <div key={l} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:8 }}>
                <Check size={13} color={u.color} style={{ marginTop:3, flexShrink:0 }}/>
                <span style={{ fontSize:13, color:C.inkLight, fontFamily:"'DM Sans',sans-serif", lineHeight:1.5 }}>{l}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────
const STEPS = [
  { n:'01', t:'Create your free account', d:'Sign up with email or Google in under 30 seconds. No credit card. No forms. No friction.' },
  { n:'02', t:'Write your first note', d:'Click New Note. Type. Format with headings, bold, and lists. Every keystroke is auto-saved.' },
  { n:'03', t:'Organise into folders', d:'Create folders and sub-folders for every area of your life. Structure it exactly how your mind works.' },
  { n:'04', t:'Search and find anything', d:'Type any word in the search bar. Jötr highlights the exact match across every note you\'ve ever written.' },
];

const HowItWorks: React.FC = () => (
  <section id="how-it-works" style={{ padding:'100px 24px', background:C.white }}>
    <div style={{ maxWidth:1120, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:64 }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,3.5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:14 }}>Up and running in 2 minutes.</h2>
        <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif", maxWidth:440, margin:'0 auto' }}>Four steps to never losing a note again.</p>
      </div>
      <div className="steps-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:24 }}>
        {STEPS.map((s,i)=>(
          <div key={i} style={{ position:'relative' }}>
            {i < STEPS.length-1 && (
              <div style={{ position:'absolute', top:28, left:'calc(50% + 36px)', right:'-50%', height:1, background:`linear-gradient(90deg,${C.amber200},${C.border})`, zIndex:0 }} className="step-connector"/>
            )}
            <div style={{ position:'relative', zIndex:1, textAlign:'center' }}>
              <div style={{ width:56, height:56, borderRadius:16, background:`linear-gradient(135deg,${C.amber200},${C.amber300})`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', boxShadow:`0 8px 20px ${C.amber300}44` }}>
                <span style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:16, color:C.white }}>{s.n}</span>
              </div>
              <h3 style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:16, color:C.ink, marginBottom:10, letterSpacing:'-0.02em' }}>{s.t}</h3>
              <p style={{ fontSize:13, color:C.muted, lineHeight:1.65, fontFamily:"'DM Sans',sans-serif" }}>{s.d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name:'Adaeze Okonkwo', role:'Business Analyst, Lagos', q:'I have hundreds of Excel formula notes. Before Jötr I spent 10 minutes hunting for one. Now I type it and it\'s there in two seconds. Game changer.' },
  { name:'Emeka Adeyemi', role:'MBA Student, Abuja', q:'I use it for every lecture. The search feature is unreal — I type a concept from any module and Jötr finds the exact sentence I wrote six months ago.' },
  { name:'Chisom Eze', role:'Entrepreneur, Port Harcourt', q:'I track clients, meeting summaries, and ideas all in one place. The fact that it syncs to my phone means I always have everything on me.' },
  { name:'Fatima Al-Hassan', role:'Secondary School Teacher, Kano', q:'Simple enough for my students, powerful enough for me. The folder system keeps everything organised and the search is honestly better than Google for my own notes.' },
];

const Testimonials: React.FC = () => (
  <section style={{ padding:'100px 24px', background:C.surface }}>
    <div style={{ maxWidth:1120, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:60 }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,3.5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:14 }}>People love finding their notes.</h2>
        <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>Real users. Real results.</p>
      </div>
      <div className="testimonial-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:20 }}>
        {TESTIMONIALS.map((t,i)=>(
          <div key={i} style={{ padding:'28px 32px', borderRadius:20, background:C.white, border:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', gap:2, marginBottom:16 }}>
              {[0,1,2,3,4].map(s=><Star key={s} size={14} fill={C.amber300} color={C.amber300}/>)}
            </div>
            <p style={{ fontSize:15, color:C.inkLight, lineHeight:1.75, marginBottom:24, fontFamily:"'DM Sans',sans-serif", fontStyle:'italic' }}>"{t.q}"</p>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:14, color:C.amber500, flexShrink:0 }}>
                {t.name.split(' ').map(w=>w[0]).join('')}
              </div>
              <div>
                <p style={{ fontSize:13, fontWeight:700, color:C.ink, margin:0, fontFamily:"'DM Sans',sans-serif" }}>{t.name}</p>
                <p style={{ fontSize:12, color:C.muted, margin:0, fontFamily:"'DM Sans',sans-serif" }}>{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── PRICING ──────────────────────────────────────────────────────────────────
const Pricing: React.FC = () => (
  <section id="pricing" style={{ padding:'100px 24px', background:C.white }}>
    <div style={{ maxWidth:800, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:56 }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,3.5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:14 }}>Free. Fully free.</h2>
        <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>No hidden limits. No credit card. No catch.</p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }} className="pricing-grid">
        {/* Free */}
        <div style={{ padding:36, borderRadius:24, border:`2.5px solid ${C.amber300}`, background:C.amber100 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <h3 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:22, color:C.ink }}>Free</h3>
            <span style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:20, background:C.amber300, color:C.white, fontFamily:"'DM Sans',sans-serif" }}>ACTIVE NOW</span>
          </div>
          <div style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:48, color:C.ink, letterSpacing:'-0.05em', marginBottom:4 }}>$0</div>
          <p style={{ fontSize:13, color:C.amber500, marginBottom:28, fontFamily:"'DM Sans',sans-serif" }}>Forever. No tricks. No expiry.</p>
          {['Unlimited notes','Unlimited folders & sub-folders','Full-text search + keyword highlights','Cloud sync — all devices','Rich text editor (headings, bold, lists)','Tags & filters','Dark mode','Offline access'].map(f=>(
            <div key={f} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:11 }}>
              <div style={{ width:18, height:18, borderRadius:5, background:C.amber300, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Check size={10} color={C.white}/>
              </div>
              <span style={{ fontSize:14, color:C.inkLight, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>{f}</span>
            </div>
          ))}
          <a href="/signup" style={{ display:'block', textAlign:'center', padding:'14px', borderRadius:12, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, textDecoration:'none', marginTop:28, fontFamily:"'DM Sans',sans-serif", boxShadow:`0 6px 20px ${C.amber300}55` }}>
            Start for free →
          </a>
        </div>
        {/* Pro */}
        <div style={{ padding:36, borderRadius:24, border:`1.5px solid ${C.border}`, background:C.surface }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <h3 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:22, color:C.ink }}>Pro</h3>
            <span style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:20, background:C.border, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>COMING SOON</span>
          </div>
          <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:4 }}>
            <span style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:48, color:C.ink, letterSpacing:'-0.05em' }}>$4</span>
            <span style={{ fontSize:16, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>/month</span>
          </div>
          <p style={{ fontSize:13, color:C.muted, marginBottom:28, fontFamily:"'DM Sans',sans-serif" }}>Launching with Version 2.</p>
          {['Everything in Free','Unlimited storage','Image attachments + OCR search','Voice notes','Note reminders','Export to PDF & Markdown','Priority support'].map(f=>(
            <div key={f} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:11 }}>
              <div style={{ width:18, height:18, borderRadius:5, background:C.border, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Check size={10} color={C.muted}/>
              </div>
              <span style={{ fontSize:14, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>{f}</span>
            </div>
          ))}
          <button disabled style={{ display:'block', width:'100%', textAlign:'center', padding:'14px', borderRadius:12, background:C.border, color:C.muted, fontWeight:700, fontSize:15, border:'none', cursor:'not-allowed', marginTop:28, fontFamily:"'DM Sans',sans-serif" }}>
            Notify me when live
          </button>
        </div>
      </div>
    </div>
  </section>
);

// ─── FAQ ──────────────────────────────────────────────────────────────────────
const FAQS = [
  { q:'Is Jötr really free?', a:'Yes — completely. Jötr launches with no paid plans, no credit card required, and no feature limits. We want you to love it first. Monetisation comes later with an optional Pro plan.' },
  { q:'How does search work?', a:'Every word in every note is indexed the moment you save it. When you type in the search bar, results appear live as you type — no pressing Enter. The matched keyword is highlighted in amber in the results list and inside the opened note.' },
  { q:'Can I access my notes from my phone?', a:'Yes. Jötr is a web app that works in any mobile browser on Android or iOS. Native Android and iOS apps are on our roadmap and coming soon.' },
  { q:'Does it work without internet?', a:'Yes. Jötr caches your notes locally using browser storage. You can read and write notes offline and everything syncs automatically when you reconnect.' },
  { q:'Is my data safe and private?', a:'Your notes are private to your account, encrypted in transit (HTTPS/TLS) and at rest. Only you can access your notes. We do not sell or share your data.' },
  { q:'Will it stay free?', a:'The core experience — unlimited notes, full-text search, folders, and sync — will always be free. A Pro plan with advanced features like image attachments, OCR search, and voice notes will be available as an optional paid upgrade in a future version.' },
];

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<number|null>(null);
  return (
    <section style={{ padding:'100px 24px', background:C.surface }}>
      <div style={{ maxWidth:720, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:56 }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,3.5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:14 }}>Got questions?</h2>
          <p style={{ fontSize:17, color:C.muted, fontFamily:"'DM Sans',sans-serif" }}>We have answers.</p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {FAQS.map((f,i)=>(
            <div key={i} style={{ borderRadius:14, border:`1px solid ${open===i?C.amber200:C.border}`, background: open===i?C.amber100:C.white, overflow:'hidden', transition:'all .2s' }}>
              <button onClick={()=>setOpen(open===i?null:i)} style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 22px', background:'none', border:'none', cursor:'pointer', gap:16 }}>
                <span style={{ fontSize:15, fontWeight:700, color:C.ink, textAlign:'left', fontFamily:"'DM Sans',sans-serif" }}>{f.q}</span>
                <div style={{ transform: open===i?'rotate(180deg)':'rotate(0)', transition:'transform .2s', flexShrink:0, color: open===i?C.amber400:C.muted }}>
                  <ChevronDown size={18}/>
                </div>
              </button>
              {open===i && (
                <div style={{ padding:'0 22px 20px' }}>
                  <p style={{ fontSize:14, color:C.muted, lineHeight:1.75, margin:0, fontFamily:"'DM Sans',sans-serif" }}>{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── FINAL CTA ────────────────────────────────────────────────────────────────
const FinalCTA: React.FC = () => (
  <section style={{ padding:'100px 24px', background:`linear-gradient(135deg,${C.amber400} 0%,${C.amber500} 100%)` }}>
    <div style={{ maxWidth:700, margin:'0 auto', textAlign:'center' }}>
      <div style={{ width:64, height:64, borderRadius:20, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 28px' }}>
        <Search size={28} color={C.white}/>
      </div>
      <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(30px,4vw,52px)', letterSpacing:'-0.04em', color:C.white, marginBottom:18, lineHeight:1.1 }}>
        Stop losing your best notes.<br/>Start finding them instead.
      </h2>
      <p style={{ fontSize:18, color:'rgba(255,255,255,0.75)', marginBottom:40, fontFamily:"'DM Sans',sans-serif", lineHeight:1.6, maxWidth:520, margin:'0 auto 40px' }}>
        Join thousands of professionals and students who write in Jötr — and actually find what they wrote.
      </p>
      <a href="/signup" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'16px 36px', borderRadius:14, background:C.white, color:C.amber400, fontWeight:800, fontSize:17, textDecoration:'none', fontFamily:"'DM Sans',sans-serif", boxShadow:'0 12px 40px rgba(0,0,0,0.15)', letterSpacing:'-0.01em' }}>
        Create your free account <ArrowRight size={18}/>
      </a>
      <p style={{ fontSize:13, color:'rgba(255,255,255,0.55)', marginTop:18, fontFamily:"'DM Sans',sans-serif" }}>Free forever · No credit card · Works on all devices</p>
    </div>
  </section>
);

// ─── FOOTER ───────────────────────────────────────────────────────────────────
const Footer: React.FC = () => (
  <footer style={{ background:C.ink, color:C.white, padding:'60px 24px 32px' }}>
    <div style={{ maxWidth:1120, margin:'0 auto' }}>
      <div className="footer-grid" style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:40, paddingBottom:48, borderBottom:`1px solid rgba(255,255,255,0.08)`, marginBottom:28 }}>
        <div>
          <Logo dark/>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.45)', lineHeight:1.7, marginTop:16, maxWidth:240, fontFamily:"'DM Sans',sans-serif" }}>
            The smart note-taking app where every word is findable. Free forever.
          </p>
          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            {[{ icon:<Twitter size={14}/>, h:'#' },{ icon:<Instagram size={14}/>, h:'#' },{ icon:<Mail size={14}/>, h:'mailto:hello@Jötr.co' }].map((s,i)=>(
              <a key={i} href={s.h} style={{ width:34, height:34, borderRadius:9, background:'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.6)', textDecoration:'none', transition:'background .15s' }}
                onMouseEnter={e=>(e.currentTarget.style.background='rgba(255,255,255,0.15)')}
                onMouseLeave={e=>(e.currentTarget.style.background='rgba(255,255,255,0.08)')}>{s.icon}</a>
            ))}
          </div>
        </div>
        {[
          { h:'Product', links:[['Features','/#features'],['How it works','/#how-it-works'],['Pricing','/#pricing'],['Changelog','/changelog']] },
          { h:'Company',  links:[['About','/about'],['Blog','/blog'],['Careers','/careers'],['Contact','/contact']] },
          { h:'Legal',    links:[['Privacy Policy','/privacy'],['Terms of Service','/terms'],['Cookie Policy','/cookies']] },
        ].map(col=>(
          <div key={col.h}>
            <p style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.3)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:16, fontFamily:"'DM Sans',sans-serif" }}>{col.h}</p>
            {col.links.map(([label,href]) => (
              <a key={label} href={href} style={{ display:'block', fontSize:14, color:'rgba(255,255,255,0.55)', textDecoration:'none', marginBottom:11, fontFamily:"'DM Sans',sans-serif", transition:'color .15s' }}
                onMouseEnter={e=>(e.currentTarget.style.color=C.white)}
                onMouseLeave={e=>(e.currentTarget.style.color='rgba(255,255,255,0.55)')}>
                {label}
              </a>
            ))}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:10, fontSize:12, color:'rgba(255,255,255,0.3)', fontFamily:"'DM Sans',sans-serif" }}>
        <span>© 2026 Jötr. All rights reserved.</span>
        <span>Made with care for note-takers everywhere.</span>
      </div>
    </div>
  </footer>
);

// ─── RESPONSIVE CSS ───────────────────────────────────────────────────────────
const ResponsiveStyles: React.FC = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    body{overflow-x:hidden;}
    .mobile-menu-btn{display:none;}
    @media(max-width:900px){
      .desktop-nav{display:none!important;}
      .mobile-menu-btn{display:block!important;}
      .hero-grid{grid-template-columns:1fr!important;gap:40px!important;}
      .hero-ctas{flex-direction:column;}
      .feature-row{grid-template-columns:1fr!important;direction:ltr!important;gap:32px!important;}
      .usecase-grid{grid-template-columns:1fr!important;}
      .steps-grid{grid-template-columns:1fr 1fr!important;}
      .step-connector{display:none!important;}
      .testimonial-grid{grid-template-columns:1fr!important;}
      .pricing-grid{grid-template-columns:1fr!important;}
      .footer-grid{grid-template-columns:1fr 1fr!important;gap:32px!important;}
    }
    @media(max-width:560px){
      .steps-grid{grid-template-columns:1fr!important;}
      .footer-grid{grid-template-columns:1fr!important;}
    }
  `}</style>
);

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const LandingPage: React.FC = () => (
  <>
    <ResponsiveStyles/>
    <Navbar/>
    <Hero/>
    <SocialProof/>
    <FeatureDeepDives/>
    <UseCases/>
    <HowItWorks/>
    <Testimonials/>
    <Pricing/>
    <FAQ/>
    <FinalCTA/>
    <Footer/>
    <CookieBanner/>
  </>
);

export default LandingPage;
